import { Breadcrumbs, useTheme } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { ContentBlockType } from '@onesource/schemas';
import { useGetLesson, useGetLessons, useGetSpace } from '@onesource/services';
import { createFileRoute, Link } from '@tanstack/react-router';
import DOMPurify from 'dompurify';

import { TextBlockContent } from '#/features/community-hub/components/lesson-detail-view/text-block-content';
import { hasHTML } from '#/utils/has-html';

/**
 * Text Content Route
 * Path: /community-hub/spaces/:id/modules/:moduleId/lessons/:lessonId/text/:contentId
 * Shows a text content block
 */
export const Route = createFileRoute(
  '/_protected/community-hub/spaces/$id/modules/$moduleId/lessons/$lessonId/text/$contentId',
)({
  component: TextContentRoute,
});

function TextContentRoute() {
  const { lessonId, contentId, moduleId, id: spaceId } = Route.useParams();
  const theme = useTheme();

  // Data fetching hooks
  const { data: lesson, isLoading: isLoadingLesson, error: lessonError } = useGetLesson(lessonId);

  const { data: module } = useGetSpace(moduleId);
  const { data: lessons } = useGetLessons(moduleId);
  const findParentLesson = lessons?.find(l => l.id === lesson?.parentId);

  const textBlock = lesson?.contentBlocks?.find(
    block => block.id === contentId && block.blockType === ContentBlockType.TEXT,
  );

  if (isLoadingLesson) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (lessonError) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load content: {lessonError.message}</Alert>
      </Box>
    );
  }

  if (!textBlock) {
    return (
      <Box p={3}>
        <Alert severity="error">Content not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs
        sx={{
          mb: { xs: 2, md: 3 },
          fontFamily: '"Figtree", sans-serif',
          fontSize: { xs: 14, md: 16 },
          lineHeight: { xs: '20px', md: '22px' },
          fontWeight: 400,
          color: theme.palette.grey[600],
          '& .MuiBreadcrumbs-separator': {
            mx: 0.6,
          },
        }}
      >
        {module?.parent && (
          <Link
            to="/community-hub/spaces/$id"
            params={{ id: spaceId }}
            style={{ color: theme.palette.grey[600], textDecoration: 'none' }}
          >
            <Typography
              variant="body2"
              sx={{ mt: hasHTML(module.parent.name) ? -0.5 : 0, fontSize: { xs: 14, md: 16 } }}
            >
              <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(module.parent.name) }} />
            </Typography>
          </Link>
        )}
        {module && (
          <Link
            to="/community-hub/spaces/$id/modules/$moduleId"
            params={{ id: spaceId, moduleId }}
            style={{ color: theme.palette.grey[600], textDecoration: 'none' }}
          >
            <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(module.name) }} />
          </Link>
        )}
        {findParentLesson && (
          <Link
            to="/community-hub/spaces/$id/modules/$moduleId"
            params={{ id: spaceId, moduleId }}
            style={{ color: theme.palette.grey[600], textDecoration: 'none' }}
          >
            {findParentLesson?.title}
          </Link>
        )}
        {lesson && (
          <Link
            to="/community-hub/spaces/$id/modules/$moduleId/lessons/$lessonId"
            params={{ id: spaceId, moduleId, lessonId }}
            style={{ color: theme.palette.grey[600], textDecoration: 'none' }}
          >
            <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(lesson?.title) }} />
          </Link>
        )}
        <Typography component="span" sx={{ fontSize: { xs: 14, md: 16 }, fontWeight: 700 }}>
          Text
        </Typography>
      </Breadcrumbs>

      <TextBlockContent block={textBlock} lessonId={lessonId} />
    </Box>
  );
}
